import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';
const UPLOAD_URL = 'https://app.thundermaxhome.com/upload.php'; 

export default function ProfilePage({ setActiveTab }) {
  const { user, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [avatarUrl, setAvatarUrl] = useState('');

  // States สำหรับ ระบบคอมมิชชันช่าง
  const [showCommissionsModal, setShowCommissionsModal] = useState(false);
  const [techCommissions, setTechCommissions] = useState([]);
  const [loadingComm, setLoadingComm] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user?.name || '',
        phone: user?.phone || '',
        email: user?.email || ''
      });
      setAvatarUrl(user?.avatar || '');
    }
  }, [user]);

  // โหลดรายการคอมมิชชันส่วนตัวของช่าง
  useEffect(() => {
    if (showCommissionsModal && user) {
      setLoadingComm(true);
      fetch(`${API_URL}?action=get_tech_commissions&tech_uid=${user.uid}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setTechCommissions(data.data || []);
          }
          setLoadingComm(false);
        })
        .catch(err => {
          console.error(err);
          setLoadingComm(false);
        });
    }
  }, [showCommissionsModal, user]);

  const updateMySQL = async (updateData) => {
    const currentUid = user?.uid || user?.id;
    if (!currentUid) {
      throw new Error('ไม่พบรหัสผู้ใช้งานในระบบ (User ID is missing)');
    }

    const res = await fetch(`${API_URL}?action=update_user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid: currentUid, ...updateData })
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.message);
    return data;
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Swal.fire({ title: 'กำลังอัปโหลดรูปโปรไฟล์...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    try {
      const apiFormData = new FormData();
      apiFormData.append('image', file);

      const response = await fetch(UPLOAD_URL, { method: 'POST', body: apiFormData });
      const result = await response.json();

      if (result.success) {
        const directLink = result.url; 
        await updateMySQL({ avatar: directLink });
        setAvatarUrl(directLink);
        Swal.fire({ icon: 'success', title: 'เปลี่ยนรูปโปรไฟล์สำเร็จ!', showConfirmButton: false, timer: 1500 });
      } else {
        throw new Error(result.message || 'อัปโหลดรูปล้มเหลว เซิร์เวอร์ปฏิเสธ');
      }
    } catch (error) {
      Swal.fire('เกิดข้อผิดพลาด', error.message, 'error');
    }
  };

  const handleSave = async () => {
    try {
      Swal.fire({ title: 'กำลังบันทึก...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      await updateMySQL({ name: formData.name, phone: formData.phone, email: formData.email });
      Swal.fire({ icon: 'success', title: 'บันทึกข้อมูลเรียบร้อย!', showConfirmButton: false, timer: 1500 });
      setIsEditing(false);
    } catch (error) {
      console.error("Save Profile Error:", error);
      Swal.fire('ข้อผิดพลาด', error.message || 'ไม่สามารถบันทึกข้อมูลได้', 'error');
    }
  };

  const confirmLogout = () => {
    Swal.fire({
      title: 'ออกจากระบบ?', text: 'คุณต้องการออกจากระบบแอปพลิเคชันใช่หรือไม่', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#e40000', cancelButtonColor: '#6b7280',
      confirmButtonText: 'ออกจากระบบ', cancelButtonText: 'ยกเลิก', reverseButtons: true
    }).then((result) => {
      if (result.isConfirmed) logout();
    });
  };

  return (
    <div className="min-h-screen bg-gray-100 flex justify-center text-left">
      <div className="w-full max-w-md bg-gray-50 flex flex-col relative shadow-2xl h-[100dvh] overflow-hidden">
        
        {/* Header ส่วนหัวโปรไฟล์ */}
        <div className="bg-blue-600 dark:bg-blue-800 text-white px-5 py-6 shadow-md z-10 text-center rounded-b-3xl shrink-0 transition-colors relative">
          <div className="relative w-24 h-24 mx-auto mb-3 group">
            <label className="cursor-pointer block w-full h-full rounded-full overflow-hidden border-4 border-white dark:border-gray-700 bg-gray-200 shadow-md">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-4xl text-blue-600 bg-white">
                  {formData.name ? formData.name.charAt(0) : '👤'}
                </div>
              )}
              <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center text-[10px] text-white opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
                📷 เปลี่ยนรูป
              </div>
              <input type="file" className="hidden" accept="image/*" onChange={handleAvatarUpload} />
            </label>
          </div>
          <h1 className="text-xl font-bold">{formData.name || 'ไม่ได้ระบุชื่อ'}</h1>
          <p className="text-blue-200 dark:text-blue-300 text-xs mt-0.5">{user?.role === 'owner' ? '👑 เจ้าของระบบ' : '🔧 ช่างเทคนิค'}</p>
        </div>

        {/* ข้อมูลโปรไฟล์และระบบแนะนำลูกค้า */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4 pb-24">
          
          {/* การ์ดข้อมูลส่วนตัว */}
          <div className="bg-white dark:bg-gray-700 p-5 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-600 transition-colors">
            <h2 className="text-gray-500 dark:text-gray-400 text-xs font-bold mb-4">ข้อมูลส่วนตัวในระบบ</h2>
            
            <div className="space-y-4">
              <div>
                <label className="text-[10px] text-gray-400 dark:text-gray-400 block mb-0.5">อีเมลล็อกอิน / แจ้งเตือน EmailJS</label>
                {isEditing ? (
                  <input type="email" className="w-full border-b-2 border-blue-500 outline-none py-1 font-semibold bg-transparent text-gray-800 dark:text-white text-sm" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
                ) : (
                  <p className="font-semibold text-gray-750 dark:text-gray-200 text-sm">{formData.email || 'ไม่มีข้อมูลอีเมล'}</p>
                )}
              </div>
              
              <div>
                <label className="text-[10px] text-gray-400 dark:text-gray-400 block mb-0.5">ชื่อ-นามสกุล (ทีมช่าง)</label>
                {isEditing ? (
                  <input type="text" className="w-full border-b-2 border-blue-500 outline-none py-1 font-semibold bg-transparent text-gray-800 dark:text-white text-sm" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                ) : (
                  <p className="font-semibold text-gray-800 dark:text-white text-sm">{formData.name || '-'}</p>
                )}
              </div>

              <div>
                <label className="text-[10px] text-gray-400 dark:text-gray-400 block mb-0.5">เบอร์โทรศัพท์มือถือ</label>
                {isEditing ? (
                  <input type="tel" className="w-full border-b-2 border-blue-500 outline-none py-1 font-semibold bg-transparent text-gray-800 dark:text-white text-sm" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                ) : (
                  <p className="font-semibold text-gray-800 dark:text-white text-sm">{formData.phone || '-'}</p>
                )}
              </div>
            </div>

            <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-600 flex justify-end">
              {isEditing ? (
                <button onClick={handleSave} className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2 rounded-xl text-xs font-bold transition cursor-pointer border-none">บันทึกข้อมูล</button>
              ) : (
                <button onClick={() => setIsEditing(true)} className="bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-200 px-5 py-2 rounded-xl text-xs font-bold hover:bg-gray-200 dark:hover:bg-gray-500 transition cursor-pointer border-none">⚙️ แก้ไขข้อมูล</button>
              )}
            </div>
          </div>

          {/* การ์ดค่าคอมมิชชันแนะนำลูกค้า */}
          <div className="bg-white dark:bg-gray-700 p-5 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-600 transition-colors">
            <h2 className="text-gray-500 dark:text-gray-400 text-xs font-bold mb-3 flex items-center gap-1.5">
              <span>💰</span> ระบบแนะนำลูกค้าใหม่ & ค่าคอม
            </h2>
            
            <div className="space-y-3.5 text-left text-xs font-bold">
              <div>
                <label className="text-[10px] text-gray-400 block mb-1">รหัสแนะนำของคุณ (Referral Code)</label>
                <div className="flex gap-2 items-center">
                  <span className="font-mono text-sm bg-gray-100 dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 flex-1 text-center font-black">
                    {user?.referral_code || `THM-${user?.username?.toUpperCase() || 'TECH'}`}
                  </span>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(user?.referral_code || `THM-${user?.username?.toUpperCase() || 'TECH'}`);
                      Swal.fire({
                        title: 'คัดลอกสำเร็จ!',
                        text: 'คัดลอกรหัสแนะนำพนักงานลงคลิปบอร์ดแล้ว',
                        icon: 'success',
                        timer: 1200,
                        showConfirmButton: false
                      });
                    }}
                    className="py-2 px-3 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg cursor-pointer border-none font-bold text-xs"
                  >
                    คัดลอก
                  </button>
                </div>
                <p className="text-[9px] text-gray-450 font-normal mt-1 leading-normal">
                  ให้ลูกค้าใหม่กรอกรหัสแนะนำนี้เมื่อจองบริการ เพื่อสะสมเงินค่าคอมแยกรายเคส
                </p>
              </div>

              <div>
                <label className="text-[10px] text-gray-400 block mb-1 mt-2">ลิงก์แนะนำ (ส่งให้ลูกค้าจองผ่านลิงก์นี้)</label>
                <div className="flex gap-2 items-center">
                  <span className="font-mono text-[10px] bg-gray-100 dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 flex-1 text-left font-medium truncate overflow-hidden">
                    {`${window.location.origin}/booking?ref=${user?.referral_code || `THM-${user?.username?.toUpperCase() || 'TECH'}`}`}
                  </span>
                  <button 
                    onClick={() => {
                      const refCode = user?.referral_code || `THM-${user?.username?.toUpperCase() || 'TECH'}`;
                      const link = `${window.location.origin}/booking?ref=${refCode}`;
                      navigator.clipboard.writeText(link);
                      Swal.fire({
                        title: 'คัดลอกลิงก์สำเร็จ!',
                        text: 'ส่งลิงก์นี้ให้ลูกค้าจองบริการได้เลย',
                        icon: 'success',
                        timer: 1200,
                        showConfirmButton: false
                      });
                    }}
                    className="py-2 px-3 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg cursor-pointer border-none font-bold text-xs whitespace-nowrap"
                  >
                    คัดลอกลิงก์
                  </button>
                </div>
              </div>

              <button 
                onClick={() => setShowCommissionsModal(true)}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black text-xs transition cursor-pointer border-none shadow-sm"
              >
                📊 เช็คยอดสะสม & สถานะค่าคอม
              </button>
            </div>
          </div>
          
          <button onClick={confirmLogout} className="w-full bg-red-100 hover:bg-red-200 dark:bg-red-900/40 text-red-600 dark:text-red-500 font-bold py-4 rounded-2xl transition text-sm cursor-pointer border-none">
            ออกจากระบบ
          </button>
        </div>

        {/* Bottom Navigation */}
        <div className="absolute bottom-0 w-full bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-around py-3 pb-6 px-2 z-20 transition-colors duration-300">
          <button onClick={() => setActiveTab('queue')} className="flex flex-col items-center text-gray-400 dark:text-gray-500 font-medium bg-transparent border-none cursor-pointer"><span className="text-2xl mb-1">🏠</span><span className="text-xs">คิวงาน</span></button>
          <button onClick={() => setActiveTab('history')} className="flex flex-col items-center text-gray-400 dark:text-gray-500 font-medium bg-transparent border-none cursor-pointer"><span className="text-2xl mb-1">📊</span><span className="text-xs">ประวัติ</span></button>
          <button onClick={() => setActiveTab('profile')} className="flex flex-col items-center text-blue-600 font-bold bg-transparent border-none cursor-pointer"><span className="text-2xl mb-1">👤</span><span className="text-xs">โปรไฟล์</span></button>
        </div>

      </div>

      {/* --- Modal เช็คสถานะค่าคอมมิชชันช่าง --- */}
      {showCommissionsModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end justify-center p-0">
          <div className="bg-white dark:bg-gray-800 rounded-t-3xl w-full max-w-md h-[85vh] flex flex-col overflow-hidden shadow-2xl animate-in slide-in-from-bottom duration-300 text-gray-900 dark:text-white">
            
            {/* Header ของ Modal */}
            <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center shrink-0">
              <h3 className="font-extrabold text-sm flex items-center gap-1.5">
                <span>💰</span> ประวัติการแนะนำลูกค้า & ค่าคอม
              </h3>
              <button 
                onClick={() => setShowCommissionsModal(false)}
                className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center font-bold text-gray-500 dark:text-gray-300 border-none cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* ส่วนสรุปยอดเงินปันผลรายสถานะ */}
            <div className="p-4 bg-gray-50 dark:bg-gray-900 grid grid-cols-3 gap-2 text-center border-b dark:border-gray-700 shrink-0">
              <div className="p-2.5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-750">
                <span className="text-[8px] text-gray-400 font-bold block uppercase tracking-wider">รออนุมัติ</span>
                <span className="text-sm font-black text-amber-500 block mt-1">
                  {techCommissions.filter(c => c.status === 'pending').reduce((sum, c) => sum + Number(c.commission_amount), 0).toLocaleString()} ฿
                </span>
              </div>
              <div className="p-2.5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-750">
                <span className="text-[8px] text-gray-400 font-bold block uppercase tracking-wider">อนุมัติแล้ว</span>
                <span className="text-sm font-black text-blue-500 block mt-1">
                  {techCommissions.filter(c => c.status === 'approved').reduce((sum, c) => sum + Number(c.commission_amount), 0).toLocaleString()} ฿
                </span>
              </div>
              <div className="p-2.5 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-750">
                <span className="text-[8px] text-gray-400 font-bold block uppercase tracking-wider">โอนเงินแล้ว</span>
                <span className="text-sm font-black text-emerald-500 block mt-1">
                  {techCommissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + Number(c.commission_amount), 0).toLocaleString()} ฿
                </span>
              </div>
            </div>

            {/* ลิสต์ประวัติแนะนำงาน */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {loadingComm ? (
                <div className="text-center py-10 text-xs text-gray-400">กำลังดึงข้อมูล...</div>
              ) : techCommissions.length > 0 ? (
                techCommissions.map(comm => (
                  <div key={comm.id} className="p-3 bg-gray-50 dark:bg-gray-700/40 border border-gray-150 dark:border-gray-700 rounded-2xl text-left flex justify-between items-center text-xs">
                    <div>
                      <p className="font-extrabold text-gray-800 dark:text-white">{comm.customer_name || 'ลูกค้าแนะนำ'}</p>
                      <p className="text-[9px] text-gray-400 font-bold mt-0.5">ค่างานบริการ: {Number(comm.service_amount).toLocaleString()} ฿</p>
                      <p className="text-[8px] text-gray-400 mt-1 font-mono">{new Date(comm.createdAt).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })}</p>
                    </div>

                    <div className="text-right">
                      <p className="font-black text-blue-600 dark:text-blue-400 text-sm">+{Number(comm.commission_amount).toLocaleString()} ฿</p>
                      <span className={`inline-block px-2 py-0.5 rounded text-[8px] font-black mt-1.5 border
                        ${comm.status === 'paid' 
                          ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' 
                          : comm.status === 'approved'
                          ? 'bg-blue-500/10 text-blue-600 border-blue-500/20'
                          : 'bg-amber-500/10 text-amber-600 border-amber-500/20'
                        }
                      `}>
                        {comm.status === 'paid' ? 'โอนแล้ว' : comm.status === 'approved' ? 'อนุมัติแล้ว' : 'รออนุมัติ'}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-gray-400 text-xs font-bold">
                  📭 ยังไม่มีข้อมูลการแนะนำลูกค้าใหม่
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
}